import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useEffect, useRef, useState } from 'react';

/**
 * Window-level file drag-and-drop (extracted from Landing so the
 * Connect-GitHub gateway shares the exact same behavior): returns whether
 * a file drag is in flight; calls onFile with the dropped File. Escape
 * cancels the drag highlight.
 */
export function useWindowFileDrop(onFile) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    dragging = _useState2[0],
    setDragging = _useState2[1];
  var dragDepth = useRef(0);
  useEffect(function () {
    var reset = function reset() {
      dragDepth.current = 0;
      setDragging(false);
    };
    var onDragEnter = function onDragEnter(e) {
      var _e$dataTransfer;
      if (!((_e$dataTransfer = e.dataTransfer) !== null && _e$dataTransfer !== void 0 && _e$dataTransfer.types.includes('Files'))) return;
      dragDepth.current += 1;
      setDragging(true);
    };
    var onDragLeave = function onDragLeave(e) {
      // relatedTarget === null means the drag left the window entirely. The
      // depth counter alone misses that (a drag that exits the browser fires
      // no balancing enter/drop), which would leave the page stuck dimmed.
      if (e.relatedTarget === null) {
        reset();
        return;
      }
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    var onDragOver = function onDragOver(e) {
      return e.preventDefault();
    };
    var onDrop = function onDrop(e) {
      var _e$dataTransfer2;
      e.preventDefault();
      reset();
      var file = (_e$dataTransfer2 = e.dataTransfer) === null || _e$dataTransfer2 === void 0 || (_e$dataTransfer2 = _e$dataTransfer2.files) === null || _e$dataTransfer2 === void 0 ? void 0 : _e$dataTransfer2[0];
      if (file) onFile(file);
    };
    var onKey = function onKey(e) {
      if (e.key === 'Escape') reset();
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    // dragend fires when a drag operation ends anywhere; blur fires when a
    // drag carries the file out to another app — both must clear the dim.
    window.addEventListener('dragend', reset);
    window.addEventListener('blur', reset);
    window.addEventListener('keydown', onKey);
    return function () {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('blur', reset);
      window.removeEventListener('keydown', onKey);
    };
  }, [onFile]);
  return dragging;
}